import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  Calendar,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
} from "lucide-react";
import { fetchEmployeeById, clearCurrentEmployee } from "../../RTK/employees/GetEmployees";
import Loader from "../../components/loader/Loader";

const EmployeeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentEmployee, loading, error } = useSelector((state) => state.employees);

  useEffect(() => {
    dispatch(fetchEmployeeById(id));
    
    return () => {
      dispatch(clearCurrentEmployee());
    };
  }, [dispatch, id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "super":
        return "bg-purple-900/30 text-purple-300 border-purple-500/30";
      case "admin":
        return "bg-blue-900/30 text-blue-300 border-blue-500/30";
      default:
        return "bg-slate-800/30 text-slate-300 border-slate-600/30";
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">{error}</div>
          <button
            onClick={() => navigate("/employees")}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg"
          >
            العودة للموظفين
          </button>
        </div>
      </div>
    );
  }

  if (!currentEmployee) {
    return null;
  }

  const employee = currentEmployee;

  return (
    <div className="min-h-screen bg-[#0f172a] p-4 md:p-6" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate("/employees")}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            العودة للموظفين
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{employee.full_name}</h1>
              <p className="text-slate-400">@{employee.username}</p>
            </div>
            <div className="mr-auto">
              {employee.is_active ? (
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>نشط</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-400">
                  <XCircle className="w-5 h-5" />
                  <span>غير نشط</span>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Basic Information */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-slate-700/50">
              <h2 className="text-xl font-semibold text-white mb-6">المعلومات الأساسية</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-sm text-slate-400">البريد الإلكتروني</p>
                      <p className="font-medium text-white">{employee.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-sm text-slate-400">رقم الهاتف</p>
                      <p className="font-medium text-white">{employee.phone_number}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-orange-400" />
                    <div>
                      <p className="text-sm text-slate-400">البلد</p>
                      <p className="font-medium text-white">{employee.country.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-sm text-slate-400">الدور</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getRoleColor(employee.role)}`}>
                        {employee.role_display}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-slate-700/50">
              <h2 className="text-xl font-semibold text-white mb-6">الصلاحيات</h2>
              
              {employee.permissions.length > 0 ? (
                <div className="space-y-3">
                  {employee.permissions.map((permission) => (
                    <div key={permission.id} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-white">{permission.name}</h3>
                          <p className="text-sm text-slate-400">{permission.category}</p>
                        </div>
                        <div className="text-xs text-slate-500">
                          <p>منح بواسطة: {permission.granted_by}</p>
                          <p>{formatDate(permission.granted_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">لا توجد صلاحيات مخصصة</p>
              )}
            </div>

            {/* Recent Activities */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-slate-700/50">
              <h2 className="text-xl font-semibold text-white mb-6">الأنشطة الأخيرة</h2>
              
              {employee.recent_activities.length > 0 ? (
                <div className="space-y-3">
                  {employee.recent_activities.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activity.success ? 'bg-green-900/30 border border-green-500/30' : 'bg-red-900/30 border border-red-500/30'}`}>
                        <Activity className={`w-4 h-4 ${activity.success ? 'text-green-400' : 'text-red-400'}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{activity.action}</p>
                        <p className="text-xs text-slate-400 mt-1">{activity.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {formatDate(activity.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">لا توجد أنشطة حديثة</p>
              )}
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Security Status */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">حالة الأمان</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">التحقق من البريد</span>
                  {employee.is_email_verified ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">التحقق من الهاتف</span>
                  {employee.is_phone_verified ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">المصادقة الثنائية</span>
                  {employee.is_2fa_enabled ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">تطبيق المصادقة</span>
                  {employee.is_authenticator_enabled ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Activity Statistics */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                إحصائيات النشاط
              </h3>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">الأنشطة الناجحة</span>
                    <span className="text-sm font-medium text-green-400">
                      {employee.activity_statistics.last_30_days.successful_activities}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full"
                      style={{
                        width: `${(employee.activity_statistics.last_30_days.successful_activities / employee.activity_statistics.last_30_days.total_activities) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">الأنشطة الفاشلة</span>
                    <span className="text-sm font-medium text-red-400">
                      {employee.activity_statistics.last_30_days.failed_activities}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-red-500 to-red-400 h-2 rounded-full"
                      style={{
                        width: `${(employee.activity_statistics.last_30_days.failed_activities / employee.activity_statistics.last_30_days.total_activities) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
                
                <div className="pt-2 border-t border-slate-700/50">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">
                      {employee.activity_statistics.last_30_days.total_activities}
                    </p>
                    <p className="text-sm text-slate-400">إجمالي الأنشطة (30 يوم)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Details */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">تفاصيل الحساب</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-400">تاريخ التسجيل</p>
                  <p className="font-medium text-white">{formatDate(employee.registration_date)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-slate-400">آخر تسجيل دخول</p>
                  <p className="font-medium text-white">{formatDate(employee.last_login)}</p>
                </div>
                
                {employee.two_fa_method && (
                  <div>
                    <p className="text-sm text-slate-400">طريقة المصادقة الثنائية</p>
                    <p className="font-medium text-white">{employee.two_fa_method}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetails;